import type React from "react";
import { useState, useRef, useEffect } from "react";
import { ChevronDown, Check } from "lucide-react";

export type SelectOption = {
  value: string;
  label: string;
};

type SelectProps = {
  label?: string;
  placeholder?: string;
  options: SelectOption[];
  value?: string;
  onChange: (value: string) => void;
  error?: string;
  fullWidth?: boolean;
  disabled?: boolean;
};

const Select: React.FC<SelectProps> = ({
  label,
  placeholder = "Select an option",
  options,
  value,
  onChange,
  error,
  fullWidth = false,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((option) => option.value === value);

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleOptionClick = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className={`relative ${fullWidth ? "w-full" : ""}`} ref={dropdownRef}>
      {label && (
        <label className="block mb-1 font-cabinetgrotesk font-medium text-lg text-soloprimary">
          {label}
        </label>
      )}

      {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
      <div
        className={`flex font-figtree items-center justify-between px-6 py-5 border  rounded-xl cursor-pointer ${
          disabled ? "opacity-20 cursor-not-allowed" : "bg-white"
        } ${error ? "border-red-500" : "border-black"}`}
        onClick={toggleDropdown}
      >
        <span
          className={`font-medium text-base tracking-[-0.32px] ${!selectedOption ? "text-black text-opacity-50" : "text-soloprimary "}`}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown
          className={`w-5 h-5 text-black transition-transform ${isOpen ? "transform rotate-180" : ""}`}
        />
      </div>

      {isOpen && (
        <div className="z-10 absolute bg-white shadow-lg mt-1 border border-gray-300 rounded-lg w-full max-h-60 font-figtree overflow-y-auto">
          {options.map((option) => (
            // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
            <div
              key={option.value}
              className={`px-4 py-2 cursor-pointer hover:bg-gray-100 flex font-medium items-center justify-between ${
                option.value === value ? "bg-gray-50" : ""
              }`}
              onClick={() => handleOptionClick(option.value)}
            >
              <span>{option.label}</span>
              {option.value === value && (
                <Check className="w-4 h-4 text-black" />
              )}
            </div>
          ))}
        </div>
      )}

      {error && <p className="mt-1 text-red-500 text-sm">{error}</p>}
    </div>
  );
};

export default Select;
